<style>
.tonal-shift-bg { background: linear-gradient(180deg, #f7f9fb 0%, #eceef0 100%); }
</style>

<!-- Hero Section -->
<section class="py-20 px-8 max-w-screen-2xl mx-auto">
<div class="grid lg:grid-cols-2 gap-16 items-center">
<div class="space-y-8">
<span class="inline-flex items-center px-3 py-1 rounded-full bg-primary-fixed text-on-primary-fixed-variant text-xs font-bold tracking-wider uppercase">Finance Module</span>
<h1 class="text-5xl md:text-6xl font-extrabold text-on-background leading-tight tracking-tight">Automated Accounting for <span class="text-primary-container">Modern Business</span></h1>
<p class="text-xl text-on-surface-variant leading-relaxed max-w-xl">From journal entries to financial statements—manage your entire fiscal cycle without manual data entry. Built for architects of scale.</p>
<div class="flex flex-wrap gap-4 pt-4">
<a routerLink="/create-account" class="px-8 py-4 bg-gradient-to-r from-primary to-primary-container text-white rounded-xl font-bold shadow-lg hover:shadow-primary-container/20 transition-all flex items-center gap-2">Get Started<span class="material-symbols-outlined text-sm" data-icon="arrow_forward">arrow_forward</span></a>
<button class="px-8 py-4 bg-surface-container-high text-on-primary-fixed-variant rounded-xl font-bold hover:bg-surface-dim transition-all flex items-center gap-2"><span class="material-symbols-outlined" data-icon="play_circle">play_circle</span> Watch Demo</button>
</div>
</div>
<div class="relative group">
<div class="absolute -inset-4 bg-primary/5 rounded-[2rem] blur-2xl group-hover:bg-primary/10 transition-colors"></div>
<div class="relative bg-surface-container-lowest rounded-[2rem] shadow-2xl overflow-hidden border border-outline-variant/10">
<img class="w-full h-auto" src="https://lh3.googleusercontent.com/aida-public/AB6AXuA428DI8719sSDZA2Npd4pUCQ__kkowFHYFj6h2EGqZXMu2qULdfIKQr4nprH5WhAVYUBf2Q9GciCN7aTp_e012uzjfSYSQOUsGc7jkw3K4cn3Yo8JqT7Ib_-dfIHrvC9AnFrADDGe7ahJ0qcmVMOQHGhXnbHHCjKI9yE4HWRQnAyOi-vVmm_VuGDdEBb52qGWAgFTRlk-RwueIMsoT9pMSVcHg6o_UQ-XQNHcX-cw9mi4WtLdUaTEhhxLkUAY21D1KMl04dm_R4-I"/>
</div>
</div>
</div>
</section>

<!-- Chart of Accounts -->
<section class="py-24 bg-surface-container-low">
<div class="max-w-screen-2xl mx-auto px-8">
<div class="text-center mb-20">
<h2 class="text-4xl font-extrabold mb-4">Intelligent Financial Architecture</h2>
<p class="text-on-surface-variant text-lg max-w-2xl mx-auto">Design a financial hierarchy that mirrors your business complexity with our intelligent architecture.</p>
</div>
<div class="grid lg:grid-cols-12 gap-12 items-center">
<div class="lg:col-span-5 space-y-12">
<div class="space-y-4">
<div class="flex items-center gap-4 text-primary">
<span class="material-symbols-outlined p-2 bg-primary-fixed rounded-lg" data-icon="account_tree">account_tree</span>
<h3 class="text-xl font-bold">Multidimensional Chart of Accounts</h3>
</div>
<p class="text-on-surface-variant">Organize your financial structure with <b>Account Groups</b> and hierarchical <b>Tree Views</b> to gain a granular perspective of assets, liabilities, and equity.</p>
</div>
<div class="space-y-4">
<div class="flex items-center gap-4 text-secondary">
<span class="material-symbols-outlined p-2 bg-secondary-container rounded-lg" data-icon="settings_suggest">settings_suggest</span>
<h3 class="text-xl font-bold">Precision Cost Center Management</h3>
</div>
<p class="text-on-surface-variant">Allocate expenses and track revenue through <b>Cost Centers</b> and <b>Cost Center Groups</b>. This feature allows for project-based accounting and departmental profitability analysis.</p>
</div>
<div class="pt-6">
<div class="p-6 bg-surface-container-lowest rounded-xl border-l-4 border-primary shadow-sm">
<p class="italic text-on-surface-variant">"The automated mapping feature reduced our month-end closing time by 40% across our international subsidiaries."</p>
<p class="mt-4 font-bold">— CFO, Global Logistics Corp</p>
</div>
</div>
</div>
<div class="lg:col-span-7 bg-surface-container-lowest p-8 rounded-[2rem] shadow-xl border border-outline-variant/10">
<div class="space-y-4">
<div class="flex items-center gap-4 p-4 bg-primary-fixed/30 rounded-xl">
<span class="material-symbols-outlined text-primary" data-icon="folder_open">folder_open</span>
<span class="font-bold text-on-primary-fixed">1000 - Assets</span>
<span class="ml-auto text-on-surface-variant font-mono">$1,240,500.00</span>
</div>
<div class="ml-12 space-y-2">
<div class="flex items-center gap-4 p-3 bg-surface-container-low rounded-lg hover:bg-surface-container-high transition-colors cursor-pointer">
<span class="material-symbols-outlined text-on-surface-variant" data-icon="chevron_right">chevron_right</span>
<span class="font-medium text-on-surface">1100 - Current Assets</span>
<span class="ml-auto text-on-surface-variant font-mono">$840,200.00</span>
</div>
<div class="ml-12 space-y-2">
<div class="flex items-center gap-4 p-2 text-sm text-on-surface-variant">
<div class="w-4 h-px bg-outline-variant"></div>
<span>1101 - Cash &amp; Bank</span>
<span class="ml-auto font-mono">$320,100.00</span>
</div>
<div class="flex items-center gap-4 p-2 text-sm text-on-surface-variant">
<div class="w-4 h-px bg-outline-variant"></div>
<span>1105 - Accounts Receivable</span>
<span class="ml-auto font-mono">$520,100.00</span>
</div>
</div>
</div>
<div class="flex items-center gap-4 p-4 bg-surface-container-low rounded-xl">
<span class="material-symbols-outlined text-error" data-icon="folder">folder</span>
<span class="font-bold text-on-surface">2000 - Liabilities</span>
<span class="ml-auto text-on-surface-variant font-mono">$450,200.00</span>
</div>
<div class="flex items-center gap-4 p-4 bg-secondary-container/30 rounded-xl">
<span class="material-symbols-outlined text-secondary" data-icon="folder">folder</span>
<span class="font-bold text-on-secondary-container">4000 - Revenue</span>
<span class="ml-auto text-on-surface-variant font-mono">$2,800,000.00</span>
</div>
</div>
</div>
</div>
</div>
</section>
<!-- Automated Ledger & Journals -->
<section class="py-24 px-8 max-w-screen-2xl mx-auto">
<div class="bg-primary-container rounded-[3rem] p-12 md:p-20 text-white overflow-hidden relative">
<div class="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
<img class="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuD1qv1P5lCVSAyhTX75BHpYEXA5rsEQw5iCV1dWEqzr-Vpi1MoNV6dVuwlVHCpZS9Qc-94Uwk7JPXABiCntgyvob5ng3QL57bfeEXbs2qh4qntS6HQPI1rkS_gEgI9IirrCkXvpSHGCmLNbpmJ3Kg8_5inA5-6yBtGCRpQGzSnJP9U_hx3U9sWTqnIht0kBbngkuKiBGj-e2C0vkrdhVXBZV3RcZNpS5x0KtphibR2nQlsxl9bweRFA0EYChP3doGsbPmef6IHvASw"/>
</div>
<div class="relative z-10 max-w-3xl">
<h2 class="text-4xl md:text-5xl font-extrabold mb-8">Zero-Touch Bookkeeping</h2>
<p class="text-xl text-primary-fixed-dim leading-relaxed mb-12">Eliminate manual entry errors with automated workflows. Every transaction from Sales and Rentals module is mirrored instantly into your books with precise double-entry logic.</p>
<div class="grid md:grid-cols-2 gap-8">
<div class="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/10">
<span class="material-symbols-outlined text-secondary-fixed text-4xl mb-4" data-icon="sync_alt">sync_alt</span>
<h3 class="text-xl font-bold mb-2">Automated Journal Entry</h3>
<p class="text-sm opacity-80">Our system ensures every debit and credit is balanced and audit-ready.</p>
</div>
<div class="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/10">
<span class="material-symbols-outlined text-secondary-fixed text-4xl mb-4" data-icon="rule">rule</span>
<h3 class="text-xl font-bold mb-2">Liquidity Management</h3>
<p class="text-sm opacity-80">Real-time tracking of <b>Payments, Receipts, and Internal Transfers</b>.</p>
</div>
</div>
</div>
</div>
</section>

<!-- Cheque & Treasury -->
<section class="py-24 px-8 max-w-screen-2xl mx-auto">
<div class="flex flex-col lg:flex-row gap-16 items-center">
<div class="lg:w-1/2 space-y-6">
<h2 class="text-4xl font-extrabold">Unified Stakeholder Directory</h2>
<p class="text-lg text-on-surface-variant leading-relaxed">Centralize <b>Customer and Vendor Groups</b>. Streamline procurement and sales by managing all financial contacts, including <b>Tenants</b> and <b>Drivers</b>, within a single ledger-linked database.</p>
<div class="grid grid-cols-2 gap-4 pt-6">
<div class="p-6 bg-surface-container rounded-2xl">
<span class="material-symbols-outlined text-primary text-3xl mb-2" data-icon="payments">payments</span>
<h4 class="font-bold">Cheque Tracking</h4>
<p class="text-xs text-on-surface-variant mt-1">Issued, Collected, &amp; Cleared states.</p>
</div>
<div class="p-6 bg-surface-container rounded-2xl">
<span class="material-symbols-outlined text-secondary text-3xl mb-2" data-icon="account_balance">account_balance</span>
<h4 class="font-bold">Bank Interfacing</h4>
<p class="text-xs text-on-surface-variant mt-1">Unified view of all cash positions.</p>
</div>
</div>
</div>
<div class="lg:w-1/2 w-full">
<div class="bg-surface-container-lowest p-6 rounded-[2rem] shadow-lg border border-outline-variant/10">
<div class="flex justify-between items-center mb-6">
<h3 class="font-bold">Cheque Registry</h3>
<button class="text-primary text-sm font-bold flex items-center gap-1"><span class="material-symbols-outlined text-sm" data-icon="add">add</span> New Issue</button>
</div>
<div class="space-y-3">
<div class="flex items-center justify-between p-4 bg-surface hover:bg-surface-container-low rounded-xl transition-all">
<div class="flex items-center gap-4">
<div class="w-10 h-10 rounded-full bg-secondary-container flex items-center justify-center"><span class="material-symbols-outlined text-secondary text-xl" data-icon="done_all">done_all</span></div>
<div><p class="font-bold text-sm">CHQ-98234</p><p class="text-xs text-on-surface-variant">Cleared - May 12</p></div>
</div>
<div class="text-right"><p class="font-bold text-sm">$12,400.00</p><p class="text-xs text-on-surface-variant">Standard Chartered</p></div>
</div>
<div class="flex items-center justify-between p-4 bg-surface hover:bg-surface-container-low rounded-xl transition-all">
<div class="flex items-center gap-4">
<div class="w-10 h-10 rounded-full bg-primary-fixed flex items-center justify-center"><span class="material-symbols-outlined text-primary text-xl" data-icon="schedule">schedule</span></div>
<div><p class="font-bold text-sm">CHQ-98241</p><p class="text-xs text-on-surface-variant">Collected - Pending Clearance</p></div>
</div>
<div class="text-right"><p class="font-bold text-sm">$4,250.00</p><p class="text-xs text-on-surface-variant">HSBC Premier</p></div>
</div>
</div>
</div>
</div>
</div>
</section>

<!-- Tax & Compliance -->
<section class="py-24 bg-on-background text-white">
<div class="max-w-screen-2xl mx-auto px-8">
<div class="grid lg:grid-cols-2 gap-16 items-center">
<div class="order-2 lg:order-1 relative">
<img class="rounded-[2rem] w-full shadow-2xl" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDdpnSAVoWAt36BrTYNX8gXLXCRtZ9HLXhzLZM-g5K9eayE383CHWhGAKBu79kQsRPXVl2yqPyfY1PpZxUJR4twplUpbsPHnJBvc74pDV-5HlaKOPM8ZbZ5zEibTK72Ncf7V3GAwb7WJ1HpF2nET03lji81U50LsAihHrmFBi9H0SQ1_dQzIUJgE_TEPOPcKoFeHiDuFah6XM_HAN6umJhxOWdguK71m_g_yN17EPPX_qOuiQNVAxEOJGoqc10lPUekB7YjryLqcPs"/>
<div class="absolute -bottom-8 -right-8 bg-primary-container p-6 rounded-2xl shadow-xl max-w-xs">
<div class="flex items-center gap-3 mb-2"><span class="material-symbols-outlined" data-icon="verified_user">verified_user</span><span class="font-bold">Audit Ready</span></div>
<p class="text-xs opacity-90">Every modification is logged with user timestamp for full accountability.</p>
</div>
</div>
<div class="order-1 lg:order-2 space-y-8">
<h2 class="text-4xl font-extrabold">Tax &amp; Compliance Engine</h2>
<p class="text-lg opacity-80">Automate regional and international <b>Tax Configurations</b>. Define tax rules once and apply them across all journals and invoices to ensure 100% compliance.</p>
<ul class="space-y-4">
<li class="flex items-start gap-4"><span class="material-symbols-outlined text-secondary-fixed" data-icon="check_circle">check_circle</span><div><p class="font-bold">Automated Filings</p><p class="text-sm opacity-60">Generate tax returns ready for submission in multiple jurisdictions.</p></div></li>
<li class="flex items-start gap-4"><span class="material-symbols-outlined text-secondary-fixed" data-icon="check_circle">check_circle</span><div><p class="font-bold">Multi-Tax Regimes</p><p class="text-sm opacity-60">Handle complex cross-border VAT rules and exemptions seamlessly.</p></div></li>
</ul>
</div>
</div>
</div>
</section>

<!-- Advanced Financial Reporting -->
<section class="py-24 px-8 max-w-screen-2xl mx-auto">
<div class="text-center mb-16">
<h2 class="text-4xl font-extrabold mb-4">Advanced Reporting Center</h2>
<p class="text-on-surface-variant text-lg">Real-time financial intelligence at your fingertips.</p>
</div>
<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
<div class="bg-surface-container-lowest p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group">
<div class="flex justify-between items-start mb-6"><div class="w-12 h-12 bg-primary-fixed/50 rounded-xl flex items-center justify-center text-primary"><span class="material-symbols-outlined" data-icon="balance">balance</span></div><span class="material-symbols-outlined text-primary-fixed-dim" data-icon="trending_up">trending_up</span></div>
<h3 class="text-xl font-bold mb-2">Balance Sheets</h3>
<p class="text-sm text-on-surface-variant mb-6">Consolidated view of assets, liabilities, and equity.</p>
<div class="h-1 bg-surface-container rounded-full overflow-hidden"><div class="w-3/4 h-full bg-primary-container"></div></div>
</div>
<div class="bg-surface-container-lowest p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group">
<div class="flex justify-between items-start mb-6"><div class="w-12 h-12 bg-secondary-container/50 rounded-xl flex items-center justify-center text-secondary"><span class="material-symbols-outlined" data-icon="query_stats">query_stats</span></div><span class="material-symbols-outlined text-secondary-fixed-dim" data-icon="show_chart">show_chart</span></div>
<h3 class="text-xl font-bold mb-2">Profit &amp; Loss</h3>
<p class="text-sm text-on-surface-variant mb-6">Monthly, quarterly, or annual performance tracking.</p>
<div class="h-1 bg-surface-container rounded-full overflow-hidden"><div class="w-1/2 h-full bg-secondary"></div></div>
</div>
<div class="bg-surface-container-lowest p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group">
<div class="flex justify-between items-start mb-6"><div class="w-12 h-12 bg-primary-fixed/50 rounded-xl flex items-center justify-center text-primary"><span class="material-symbols-outlined" data-icon="monetization_on">monetization_on</span></div><span class="material-symbols-outlined text-primary-fixed-dim" data-icon="stacked_line_chart">stacked_line_chart</span></div>
<h3 class="text-xl font-bold mb-2">Cash Flow</h3>
<p class="text-sm text-on-surface-variant mb-6">Monitor liquidity and operating cash movements.</p>
<div class="h-1 bg-surface-container rounded-full overflow-hidden"><div class="w-2/3 h-full bg-primary"></div></div>
</div>
<div class="bg-surface-container-lowest p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all group">
<div class="flex justify-between items-start mb-6"><div class="w-12 h-12 bg-on-background/10 rounded-xl flex items-center justify-center text-on-background"><span class="material-symbols-outlined" data-icon="receipt_long">receipt_long</span></div><span class="material-symbols-outlined text-outline-variant" data-icon="data_exploration">data_exploration</span></div>
<h3 class="text-xl font-bold mb-2">Trial Balances</h3>
<p class="text-sm text-on-surface-variant mb-6">Instant diagnostic check of ledger balancing.</p>
<div class="h-1 bg-surface-container rounded-full overflow-hidden"><div class="w-full h-full bg-on-background"></div></div>
</div>
</div>
</section>

<!-- Asset Management & Depreciation -->
<section class="py-24 bg-surface-container-high/30">
<div class="max-w-screen-2xl mx-auto px-8">
<div class="flex flex-col md:flex-row items-center justify-between gap-12">
<div class="md:w-1/2">
<h2 class="text-3xl font-extrabold mb-6">Asset Lifecycle Management</h2>
<p class="text-on-surface-variant text-lg leading-relaxed mb-8">Register fixed assets and let ZAD ERP handle the complex depreciation schedules automatically, ensuring accurate valuation on your balance sheets.</p>
<div class="flex gap-4">
<div class="flex flex-col"><span class="text-2xl font-bold text-primary">SLM / DDB</span><span class="text-xs text-on-surface-variant uppercase tracking-wider font-bold">Depreciation Methods</span></div>
<div class="w-px h-12 bg-outline-variant"></div>
<div class="flex flex-col"><span class="text-2xl font-bold text-primary">1-Click</span><span class="text-xs text-on-surface-variant uppercase tracking-wider font-bold">Write-off Process</span></div>
</div>
</div>
<div class="md:w-1/2 grid grid-cols-2 gap-4">
<div class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10"><span class="material-symbols-outlined text-primary mb-2" data-icon="precision_manufacturing">precision_manufacturing</span><h4 class="font-bold">Plant &amp; Machinery</h4></div>
<div class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10"><span class="material-symbols-outlined text-primary mb-2" data-icon="directions_car">directions_car</span><h4 class="font-bold">Vehicle Fleet</h4></div>
<div class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10"><span class="material-symbols-outlined text-primary mb-2" data-icon="laptop_mac">laptop_mac</span><h4 class="font-bold">IT Infrastructure</h4></div>
<div class="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10"><span class="material-symbols-outlined text-primary mb-2" data-icon="domain">domain</span><h4 class="font-bold">Real Estate</h4></div>
</div>
</div>
</div>
</section>
<!-- CTA Section -->
<section class="py-24 px-8 max-w-screen-2xl mx-auto text-center">
<div class="bg-gradient-to-br from-primary-container to-primary p-16 rounded-[3rem] text-white">
<h2 class="text-4xl md:text-5xl font-extrabold mb-8">Ready to automate your accounting?</h2>
<p class="text-xl opacity-90 mb-12 max-w-2xl mx-auto">Join the new era of financial management. Scale your business with data-driven confidence.</p>
<div class="flex justify-center gap-4">
<a routerLink="/create-account" class="bg-white text-primary px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-2xl transition-all active:scale-95">Get Started Now</a>
<a routerLink="/contact" class="bg-white/10 backdrop-blur-md text-white border border-white/20 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all">Contact Sales</a>
</div>
</div>
</section>

<!-- Frequently Asked Questions Section (Dynamically Bound) -->
<section class="py-24 bg-surface-container-low font-headline">
<div class="max-w-3xl mx-auto px-8">
<div class="text-center mb-16">
<h2 class="text-4xl font-extrabold mb-4">Frequently Asked Questions</h2>
<p class="text-on-surface-variant font-body">Everything you need to know about our Finance &amp; Core Accounting Module.</p>
</div>
<div class="space-y-4 font-body">
  <details *ngFor="let faq of faqs" class="faq-accordion group bg-surface-container-lowest rounded-2xl shadow-sm overflow-hidden border border-transparent hover:border-outline-variant transition-all duration-300">
    <summary class="flex justify-between items-center p-6 cursor-pointer list-none">
    <h3 class="text-lg font-bold text-on-surface">{{faq.question}}</h3>
    <span class="faq-icon material-symbols-outlined text-primary transition-transform duration-300">expand_more</span>
    </summary>
    <div class="px-6 pb-6 text-on-surface-variant leading-relaxed">{{faq.answer}}</div>
  </details>
</div>
</div>
</section>
